type TranslateFunction = (key: string, options?: Record<string, any>) => string;
import AutomateImage from '../../assets/collectionImages/tech.svg';
import WiimImage from '../../assets/subscriptionImages/wiim.svg';
import LeaderboardImage from '../../assets/collectionImages/leaderboard.svg';
import AlertsImage from '../../assets/collectionImages/bell.svg';
import rocketImage from '../../assets/collectionImages/growth.svg';
import { InvestorType } from '../../utils/smartChatPrompts';

export interface FeatureItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

export interface Testimonial {
  title: string;
  author: string;
  content: string;
}

export interface SocialProofStat {
  icon: string;
  value: string;
  label: string;
}

// Default features (used for 'other' and fallback)
export const getPaymentFeatures = (t: TranslateFunction): FeatureItem[] => [
  {
    id: 'unlimited-chats',
    title: t('subscription.feature.unlimitedChats.title'),
    description: t('subscription.feature.unlimitedChats.desc'),
    imageUrl: AutomateImage,
  },
  {
    id: 'ai-picks',
    title: t('subscription.feature.aiPicks.title'),
    description: t('subscription.feature.aiPicks.desc'),
    imageUrl: LeaderboardImage,
  },
  {
    id: 'portfolio-sync',
    title: t('subscription.feature.portfolioSync.title'),
    description: t('subscription.feature.portfolioSync.desc'),
    imageUrl: rocketImage,
  },
  {
    id: 'news-on-graph',
    title: t('subscription.feature.newsOnGraph.title'),
    description: t('subscription.feature.newsOnGraph.desc'),
    imageUrl: WiimImage,
  },
  {
    id: 'smart-summaries',
    title: t('subscription.feature.smartSummaries.title'),
    description: t('subscription.feature.smartSummaries.desc'),
    imageUrl: AlertsImage,
  },
];

// Keep static arrays for backward compat (English fallback)
export const PAYMENT_FEATURES: FeatureItem[] = [
  {
    id: 'unlimited-chats',
    title: 'Unlimited AI chats',
    description: 'Ask anything about any stock — no daily limits',
    imageUrl: AutomateImage,
  },
  {
    id: 'ai-picks',
    title: 'AI-curated stock picks',
    description: 'Browse daily picks with clear buy/sell reasoning',
    imageUrl: LeaderboardImage,
  },
  {
    id: 'portfolio-sync',
    title: 'Connect your portfolio',
    description: 'Link your brokerage for personalized insights on your holdings',
    imageUrl: rocketImage,
  },
  {
    id: 'news-on-graph',
    title: 'News on every price chart',
    description: 'See exactly what news moved the stock on any date',
    imageUrl: WiimImage,
  },
  {
    id: 'smart-summaries',
    title: 'Personal market updates',
    description: 'Get a daily briefing on your portfolio and watchlist',
    imageUrl: AlertsImage,
  },
];

// Personalized features by investor type
export const getInvestorTypeFeatures = (t: TranslateFunction): Record<InvestorType, FeatureItem[]> => ({
  'new-investor': [
    {
      id: 'unlimited-chats',
      title: t('subscription.feature.newInvestor.unlimitedChats.title'),
      description: t('subscription.feature.newInvestor.unlimitedChats.desc'),
      imageUrl: AutomateImage,
    },
    {
      id: 'ai-picks',
      title: t('subscription.feature.newInvestor.aiPicks.title'),
      description: t('subscription.feature.newInvestor.aiPicks.desc'),
      imageUrl: LeaderboardImage,
    },
    {
      id: 'portfolio-sync',
      title: t('subscription.feature.newInvestor.portfolioSync.title'),
      description: t('subscription.feature.newInvestor.portfolioSync.desc'),
      imageUrl: rocketImage,
    },
    {
      id: 'news-on-graph',
      title: t('subscription.feature.newInvestor.newsOnGraph.title'),
      description: t('subscription.feature.newInvestor.newsOnGraph.desc'),
      imageUrl: WiimImage,
    },
    {
      id: 'smart-summaries',
      title: t('subscription.feature.newInvestor.smartSummaries.title'),
      description: t('subscription.feature.newInvestor.smartSummaries.desc'),
      imageUrl: AlertsImage,
    },
  ],
  'active-trader': [
    {
      id: 'unlimited-chats',
      title: t('subscription.feature.activeTrader.unlimitedChats.title'),
      description: t('subscription.feature.activeTrader.unlimitedChats.desc'),
      imageUrl: AutomateImage,
    },
    {
      id: 'ai-picks',
      title: t('subscription.feature.activeTrader.aiPicks.title'),
      description: t('subscription.feature.activeTrader.aiPicks.desc'),
      imageUrl: LeaderboardImage,
    },
    {
      id: 'portfolio-sync',
      title: t('subscription.feature.activeTrader.portfolioSync.title'),
      description: t('subscription.feature.activeTrader.portfolioSync.desc'),
      imageUrl: rocketImage,
    },
    {
      id: 'news-on-graph',
      title: t('subscription.feature.activeTrader.newsOnGraph.title'),
      description: t('subscription.feature.activeTrader.newsOnGraph.desc'),
      imageUrl: WiimImage,
    },
    {
      id: 'smart-summaries',
      title: t('subscription.feature.activeTrader.smartSummaries.title'),
      description: t('subscription.feature.activeTrader.smartSummaries.desc'),
      imageUrl: AlertsImage,
    },
  ],
  researcher: [
    {
      id: 'unlimited-chats',
      title: t('subscription.feature.researcher.unlimitedChats.title'),
      description: t('subscription.feature.researcher.unlimitedChats.desc'),
      imageUrl: AutomateImage,
    },
    {
      id: 'ai-picks',
      title: t('subscription.feature.researcher.aiPicks.title'),
      description: t('subscription.feature.researcher.aiPicks.desc'),
      imageUrl: LeaderboardImage,
    },
    {
      id: 'portfolio-sync',
      title: t('subscription.feature.researcher.portfolioSync.title'),
      description: t('subscription.feature.researcher.portfolioSync.desc'),
      imageUrl: rocketImage,
    },
    {
      id: 'news-on-graph',
      title: t('subscription.feature.researcher.newsOnGraph.title'),
      description: t('subscription.feature.researcher.newsOnGraph.desc'),
      imageUrl: WiimImage,
    },
    {
      id: 'smart-summaries',
      title: t('subscription.feature.researcher.smartSummaries.title'),
      description: t('subscription.feature.researcher.smartSummaries.desc'),
      imageUrl: AlertsImage,
    },
  ],
  other: getPaymentFeatures(t),
});

/**
 * Get features for a specific investor type
 * Falls back to default features if type is unknown
 */
export const getFeaturesForInvestorType = (
  investorType?: InvestorType | string,
  t?: TranslateFunction,
): FeatureItem[] => {
  if (t) {
    const features = getInvestorTypeFeatures(t);
    if (!investorType) return getPaymentFeatures(t);
    return features[investorType as InvestorType] || getPaymentFeatures(t);
  }
  return PAYMENT_FEATURES;
};

export const getTestimonials = (t: TranslateFunction): Testimonial[] => [
  {
    title: t('subscription.testimonial1.title'),
    author: 'Biggie64220',
    content: t('subscription.testimonial1.content'),
  },
  {
    title: t('subscription.testimonial2.title'),
    author: 'gtcolmenero',
    content: t('subscription.testimonial2.content'),
  },
  {
    title: t('subscription.testimonial3.title'),
    author: 'Shane Egan',
    content: t('subscription.testimonial3.content'),
  },
  {
    title: t('subscription.testimonial4.title'),
    author: 'Ryan',
    content: t('subscription.testimonial4.content'),
  },
  {
    title: t('subscription.testimonial5.title'),
    author: 'Paul Rees',
    content: t('subscription.testimonial5.content'),
  },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    title: 'Takes a genius to make it simple',
    author: 'Biggie64220',
    content:
      "One of my favorite apps, Bloom takes mountains of data and boils it down into an easily digestible format. I've only found 4-5 sources that do this, in 15 years of investing. I use to waste so much time reading useless information. Now I focus solely on the fundamentals. Apps like Bloom make eliminating bad stocks so easy, helping me catch red flags or spot opportunities I wouldn't ordinarily come across immediately.",
  },
  {
    title: 'Getting better every day',
    author: 'gtcolmenero',
    content:
      'I have been with you for a couple of years now, in the beginning not so much as you catered more to long traders, but as time has gone by your model has changed significantly. I find myself referring to you more and more often. KEEP UP THE GOOD WORK GETTING BETTER EVERY DAY DONT STOP!',
  },
  {
    title: 'Excellent app for budding investors',
    author: 'Shane Egan',
    content:
      'Gives valuable information and news updates which assist in investing in quality stocks. Thanks for all your hard work.',
  },
  {
    title: 'Incredible app!',
    author: 'Ryan',
    content: "I'm new to stock investing and I've found this to be a wonderful tool :)",
  },
  {
    title: 'Great app',
    author: 'Paul Rees',
    content: "It's very clean and quick to navigate around. I'll definitely keep it and prefer it over Yahoo finance.",
  },
];

export const SOCIAL_PROOF_STATS: SocialProofStat[] = [
  {
    icon: '💼',
    value: '10,000+',
    label: 'Investors',
  },
  {
    icon: '🏆',
    value: "Editor's",
    label: 'Choice',
  },
  {
    icon: '🇺🇸',
    value: 'NYC',
    label: 'Based',
  },
];

export const getSocialProofStats = (t: TranslateFunction): SocialProofStat[] => [
  {
    icon: '💼',
    value: '10,000+',
    label: t('subscription.socialProof.investorsLabel'),
  },
  {
    icon: '🏆',
    value: t('subscription.socialProof.editorsValue'),
    label: t('subscription.socialProof.editorsLabel'),
  },
  {
    icon: '🇺🇸',
    value: 'NYC',
    label: t('subscription.socialProof.nycLabel'),
  },
];
